import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Spin,
  Row,
  Col,
  Button,
} from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../redux/message.reducer';
import './RepairFormModal.css';
import './RepairViewModal.css';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'inprogress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

function normalizeRepairStatus(s) {
  const valid = ['pending', 'inprogress', 'done'];
  if (valid.includes(s)) return s;
  if (s === 'send_to_repair' || s === 'in_progress') return 'inprogress';
  if (s === 'repaired' || s === 'completed') return 'done';
  return 'pending';
}

function ViewPhotoThumbs({ urls, onPick }) {
  const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
  if (list.length === 0) {
    return <span className="repair-view-photo-empty">—</span>;
  }
  return (
    <ul className="repair-form-file-input-list">
      {list.map((url, i) => (
        <li key={`${url}-${i}`} className="repair-form-file-input-thumb-item">
          <button
            type="button"
            className="repair-view-photo-thumb-btn"
            onClick={() => onPick(i)}
            aria-label={`View photo ${i + 1}`}
          >
            <img src={url} alt="" className="repair-form-file-input-thumb" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function RepairViewModal({ open, repairId, onClose, onEdit }) {
  const dispatch = useDispatch();
  const [repair, setRepair] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setRepair(null);
      setPreviewOpen(false);
      return;
    }
    if (!repairId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`/repairs/${repairId}`)
      .then((res) => {
        if (!cancelled) setRepair(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err.response?.data?.error || 'Failed to load repair';
          dispatch(setMessage({ message: msg, type: 'error' }));
          onClose?.();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, repairId, dispatch, onClose]);

  const itemImages = repair && Array.isArray(repair.itemImages) ? repair.itemImages.filter(Boolean) : [];

  return (
    <>
      <Modal
        title="View repair"
        open={open}
        onCancel={onClose}
        footer={null}
        width={920}
        destroyOnClose
        className="repair-form-modal"
        maskClosable
      >
        <Spin spinning={loading}>
          {!repair ? (
            <div className="repair-view-modal-loading" />
          ) : (
            <Form
              key={repair._id}
              layout="vertical"
              requiredMark={false}
              disabled
              className="repair-form-modal-body"
              initialValues={{
                issue: repair.issue ?? '',
                givenTo: repair.givenTo ?? '',
                status: normalizeRepairStatus(repair.status),
              }}
            >
              <Row gutter={[20, 20]} align="top" wrap className="repair-form-modal-inline-row">
                <Col xs={24} md={24} lg={5} xl={4} className="repair-form-modal-photo">
                  <Form.Item label="Photos">
                    <ViewPhotoThumbs
                      urls={itemImages}
                      onPick={(i) => {
                        setPreviewIndex(i);
                        setPreviewOpen(true);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={9} xl={10}>
                  <Form.Item name="issue" label="Remark">
                    <Input size="large" readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={5} xl={5}>
                  <Form.Item name="givenTo" label="Given to">
                    <Input size="large" readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={5} xl={5}>
                  <Form.Item name="status" label="Status">
                    <Select size="large" options={STATUS_OPTIONS} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <div className="repair-form-modal-actions">
                <Button type="default" size="large" onClick={onClose} className="app-btn-cancel">
                  Close
                </Button>
                <Button
                  type="primary"
                  size="large"
                  className="app-btn-primary"
                  onClick={() => {
                    onClose?.();
                    onEdit?.(repairId);
                  }}
                >
                  Edit
                </Button>
              </div>
            </Form>
          )}
        </Spin>
      </Modal>

      <Modal
        title={itemImages.length ? `Photo ${previewIndex + 1} of ${itemImages.length}` : 'Photo'}
        open={previewOpen && itemImages.length > 0}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width="auto"
        centered
        zIndex={1100}
        className="repair-form-modal"
        styles={{ body: { padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 } }}
      >
        {itemImages.length > 0 && (
          <>
            {previewIndex > 0 && (
              <Button type="text" icon={<LeftOutlined />} onClick={() => setPreviewIndex((i) => i - 1)} />
            )}
            <img
              src={itemImages[previewIndex]}
              alt={`Photo ${previewIndex + 1}`}
              style={{ maxHeight: '80vh', maxWidth: '70vw', objectFit: 'contain' }}
            />
            {previewIndex < itemImages.length - 1 && (
              <Button type="text" icon={<RightOutlined />} onClick={() => setPreviewIndex((i) => i + 1)} />
            )}
          </>
        )}
      </Modal>
    </>
  );
}
